import React, { useState, useEffect } from 'react';
import { Card } from '../../components/base/Card';
import { Button } from '../../components/base/Button';
import { SEO } from '../../utils/seo';
import { supabase } from '../../lib/supabase';

export const QuotePage: React.FC = () => {
  const [pageData, setPageData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    serviceType: '',
    material: '',
    quantity: '',
    deadline: '',
    description: '',
    hasDrawing: false,
    urgentProject: false
  });

  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const { data, error } = await supabase
        .from('pages')
        .select('*')
        .eq('slug', 'teklif-al')
        .eq('is_published', true)
        .single();

      if (error) throw error;
      if (data) {
        setPageData(data);
      }
    } catch (error) {
      console.error('Sayfa yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic here
    console.log('Quote form submitted:', formData, files);
  };

  const services = [
    { value: 'cnc-torna', label: 'CNC Torna' },
    { value: 'cnc-freze', label: 'CNC Dik İşleme Merkezi' },
    { value: 'universal', label: 'Üniversal Torna & Freze' },
    { value: 'kaynak', label: 'Kaynak İşlemleri' },
    { value: '3d-baski', label: '3D Printer & Prototipleme' },
    { value: 'karma', label: 'Karma İşlemler' }
  ];

  const materials = [
    'Çelik (St37, St52, 42CrMo4)',
    'Paslanmaz Çelik (304, 316, 430)',
    'Alüminyum (6061, 7075, 2024)',
    'Pirinç ve Bronz',
    'Titanyum',
    'Plastik (POM, PA6, PEEK)',
    'Diğer (Açıklamada belirtiniz)'
  ];

  const advantages = [
    {
      icon: 'ri-time-line',
      title: 'Hızlı Yanıt',
      description: 'Aynı gün içinde detaylı teklif'
    },
    {
      icon: 'ri-calculator-line',
      title: 'Şeffaf Fiyat',
      description: 'Gizli maliyet yok, net fiyatlar'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Kalite Garantisi',
      description: 'ISO standartlarında üretim'
    },
    {
      icon: 'ri-truck-line',
      title: 'Zamanında Teslimat',
      description: '%98 zamanında teslimat oranı'
    }
  ];

  const steps = [
    {
      step: '01',
      title: 'Teklif Formu',
      description: 'Proje detaylarınızı ve çizimlerinizi gönderin'
    },
    {
      step: '02',
      title: 'Teknik İnceleme',
      description: 'Uzman ekibimiz projenizi detaylı inceler'
    },
    {
      step: '03',
      title: 'Fiyat Hesaplama',
      description: 'Maliyet analizi ve fiyat hesaplaması yapılır'
    },
    {
      step: '04',
      title: 'Teklif Sunumu',
      description: 'Detaylı teklif ve teslimat süresi bildirilir'
    }
  ];

  const quoteSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Teklif Al - 3DP Makina",
    "description": "Çiziminizi yükleyin, aynı gün teklif alın. Hassas imalat, kaliteli kaynak ve hızlı prototipleme için detaylı fiyat teklifi hazırlıyoruz.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/teklif-al`,
    "mainEntity": {
      "@type": "Service",
      "name": "Ücretsiz Teklif Hazırlama",
      "description": "Aynı gün içinde detaylı teklif hazırlama hizmeti",
      "provider": {
        "@type": "Organization",
        "name": "3DP Makina"
      }
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Teklif Al | Ücretsiz Fiyat Teklifi - 3DP Makina"
        description="Çiziminizi yükleyin, aynı gün teklif alın. Hassas imalat, kaliteli kaynak ve hızlı prototipleme için detaylı fiyat teklifi hazırlıyoruz."
        keywords="teklif al, fiyat teklifi, CNC teklif, kaynak fiyat, 3D baskı teklif, ücretsiz teklif"
        canonical={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/teklif-al`}
        ogTitle="Teklif Al | Ücretsiz Fiyat Teklifi - 3DP Makina"
        ogDescription="Çiziminizi yükleyin, aynı gün teklif alın. Hassas imalat, kaliteli kaynak ve hızlı prototipleme için detaylı fiyat teklifi."
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/teklif-al`}
        jsonLd={quoteSchema}
      />

      {/* Hero Section */}
      <section 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.7), rgba(22, 22, 22, 0.7)), url('https://public.readdy.ai/ai/img_res/3eb65042c6026d5b8728286d209c8e71.jpg')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Teklif Al
            </h1>
            <p className="text-xl leading-relaxed">
              Çiziminizi yükleyin, aynı gün teklif alın. Hassas imalat, kaliteli kaynak 
              ve hızlı prototipleme için detaylı fiyat teklifi hazırlıyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {advantages.map((advantage, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${advantage.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-[#161616] mb-2">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 text-sm">
                  {advantage.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
                Teklif Formu
              </h2>
              <p className="text-xl text-gray-600">
                Projeniz hakkında detaylı bilgi verin, size en uygun teklifi hazırlayalım.
              </p>
            </div>

            <Card>
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h3 className="text-xl font-semibold text-[#161616] mb-6 pb-3 border-b border-gray-200">
                    İletişim Bilgileri
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ad Soyad *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="Adınız ve soyadınız"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        E-posta *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="ornek@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefon *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="+90 5XX XXX XX XX"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Firma
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="Firma adınız (opsiyonel)"
                      />
                    </div>
                  </div>
                </div>

                {/* Project Details */}
                <div>
                  <h3 className="text-xl font-semibold text-[#161616] mb-6 pb-3 border-b border-gray-200">
                    Proje Detayları
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Hizmet Türü *
                      </label>
                      <select
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                      >
                        <option value="">Hizmet türünü seçiniz</option>
                        {services.map((service) => (
                          <option key={service.value} value={service.value}>
                            {service.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Malzeme *
                      </label>
                      <select
                        name="material"
                        value={formData.material}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                      >
                        <option value="">Malzeme seçiniz</option>
                        {materials.map((material, index) => (
                          <option key={index} value={material}>
                            {material}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Adet *
                      </label>
                      <input
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleInputChange}
                        required
                        min="1"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                        placeholder="Kaç adet üretilecek?"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Teslimat Tarihi
                      </label>
                      <input
                        type="date"
                        name="deadline"
                        value={formData.deadline}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* File Upload */}
                <div>
                  <h3 className="text-xl font-semibold text-[#161616] mb-6 pb-3 border-b border-gray-200">
                    Teknik Çizimler ve Dosyalar
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dosya Yükleme (PDF, DWG, STEP, STL)
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#C1121F] transition-colors">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          multiple
                          accept=".pdf,.dwg,.step,.stp,.stl,.iges,.igs"
                          className="hidden"
                          id="file-upload"
                        />
                        <label htmlFor="file-upload" className="cursor-pointer">
                          <i className="ri-upload-cloud-2-line text-4xl text-gray-400 mb-4 block"></i>
                          <p className="text-gray-600 mb-2">
                            Dosyalarınızı buraya sürükleyin veya tıklayın
                          </p>
                          <p className="text-sm text-gray-500">
                            PDF, DWG, STEP, STL formatları desteklenir (Maks. 10MB)
                          </p>
                        </label>
                      </div>
                      {files.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Yüklenen Dosyalar:</p>
                          <ul className="space-y-1">
                            {files.map((file, index) => (
                              <li key={index} className="text-sm text-gray-600 flex items-center">
                                <i className="ri-file-line mr-2 text-[#C1121F]"></i>
                                {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div>
                  <h3 className="text-xl font-semibold text-[#161616] mb-6 pb-3 border-b border-gray-200">
                    Ek Bilgiler
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Proje Açıklaması
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        rows={6}
                        maxLength={500}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#C1121F] focus:border-transparent resize-none"
                        placeholder="Projeniz hakkında detaylı bilgi verin... Özel toleranslar, yüzey işlemleri, montaj gereksinimleri vb. (Maksimum 500 karakter)"
                      />
                      <div className="text-right text-sm text-gray-500 mt-1">
                        {formData.description.length}/500
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="hasDrawing"
                          checked={formData.hasDrawing}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#C1121F] border-gray-300 rounded focus:ring-[#C1121F]"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                          Teknik çizimim mevcut
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="urgentProject"
                          checked={formData.urgentProject}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-[#C1121F] border-gray-300 rounded focus:ring-[#C1121F]"
                        />
                        <label className="ml-2 text-sm text-gray-700">
                          Acil proje (Ek ücret uygulanabilir)
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-6 border-t border-gray-200">
                  <Button type="submit" variant="primary" size="lg" className="w-full">
                    <i className="ri-send-plane-line mr-2"></i>
                    Teklif Talebini Gönder
                  </Button>
                  <p className="text-sm text-gray-500 text-center mt-4">
                    Teklif talebinizi gönderdikten sonra 24 saat içinde size dönüş yapacağız.
                  </p>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Teklif Süreci
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Teklif talebinizden üretime kadar takip edeceğimiz adımlar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-4">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-[#C1121F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Hızlı Teklif İçin Hemen Arayın
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Acil projeleriniz için telefon ile hızlı teklif alabilirsiniz.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="secondary" 
              size="lg"
              onClick={() => window.open('tel:+905414565635', '_self')}
            >
              <i className="ri-phone-line mr-2"></i>
              0541 456 56 35
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-white text-white hover:bg-white hover:text-[#C1121F]"
              onClick={() => window.open('https://wa.me/905414565635', '_blank')}
            >
              <i className="ri-whatsapp-line mr-2"></i>
              WhatsApp ile Yaz
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
